import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, ExternalLink } from "lucide-react";

// Two offices are surfaced on the contact section — Bratislava sales office
// and Lehnice registered office (matches Google Business Profile / GMB).
// Embed uses google.com/maps with output=embed which does NOT require an API
// key (rate-limited, but fine at our traffic). If we ever add the Places API
// key, we can upgrade to the Maps Embed API for richer pins.
const OFFICES = [
  {
    key: "bratislava",
    label: "Bratislava",
    role: "Kancelária a obchod",
    address: "Kopčianska 10, 851 01 Bratislava",
    hours: "Po-Pi 07:00 – 19:00 · So 08:00 – 14:00",
    phone: "0907 64 99 64",
    mapEmbed: "https://www.google.com/maps?q=Kop%C4%8Dianska+10%2C+Bratislava&z=15&output=embed",
    mapLink: "https://www.google.com/maps/search/?api=1&query=Kop%C4%8Dianska+10%2C+Bratislava",
  },
  {
    key: "lehnice",
    label: "Lehnice",
    role: "Registrované sídlo firmy",
    address: "Sása 403, 930 37 Lehnice",
    hours: "Po-Pi 07:00 – 19:00 · So 08:00 – 14:00",
    phone: "0907 64 99 64",
    mapEmbed: "https://www.google.com/maps?q=S%C3%A1sa+403%2C+930+37+Lehnice&z=15&output=embed",
    mapLink: "https://maps.app.goo.gl/6PaRoCStWtNiQxtn9",
  },
];

export default function OfficesMap() {
  const [active, setActive] = useState(OFFICES[0].key);
  const office = OFFICES.find((o) => o.key === active) || OFFICES[0];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="mt-16"
      data-testid="offices-map"
    >
      <div className="mb-6">
        <span className="mono text-xs text-[#00E5FF] uppercase tracking-[0.2em]">
          Naše pobočky
        </span>
        <h3 className="text-2xl sm:text-3xl font-black tracking-tight mt-2">
          Nájdete nás v dvoch mestách
        </h3>
      </div>

      {/* tabs */}
      <div
        role="tablist"
        aria-label="Pobočky"
        className="inline-flex gap-1 p-1 bg-white/5 border border-white/10 rounded-2xl mb-5"
      >
        {OFFICES.map((o) => (
          <button
            key={o.key}
            role="tab"
            aria-selected={active === o.key}
            onClick={() => setActive(o.key)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              active === o.key
                ? "bg-[#00E5FF] text-black shadow-[0_0_24px_rgba(0,229,255,0.25)]"
                : "text-zinc-300 hover:text-white"
            }`}
            data-testid={`office-tab-${o.key}`}
          >
            {o.label}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-5 gap-5">
        {/* Map */}
        <div className="md:col-span-3 rounded-2xl overflow-hidden border border-white/10 bg-white/5">
          <iframe
            key={office.key}
            title={`Mapa pobočky ${office.label}`}
            src={office.mapEmbed}
            width="100%"
            height="360"
            style={{ border: 0, display: "block" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            data-testid={`office-iframe-${office.key}`}
          />
        </div>

        {/* Details */}
        <div className="md:col-span-2 glass-card p-6 flex flex-col gap-4" data-testid={`office-details-${office.key}`}>
          <div>
            <span className="mono text-[11px] text-[#00E5FF] uppercase tracking-widest">
              {office.role}
            </span>
            <h4 className="text-xl font-bold mt-1">{office.label}</h4>
          </div>

          <div className="flex items-start gap-3">
            <MapPin size={18} className="text-[#00E5FF] shrink-0 mt-0.5" />
            <p className="text-sm text-zinc-200 leading-relaxed">{office.address}</p>
          </div>

          <div className="flex items-start gap-3">
            <Clock size={18} className="text-[#00E5FF] shrink-0 mt-0.5" />
            <p className="text-sm text-zinc-300">{office.hours}</p>
          </div>

          <a
            href={`tel:${office.phone.replace(/\s/g, "")}`}
            className="flex items-start gap-3 group"
            data-testid={`office-phone-${office.key}`}
          >
            <Phone size={18} className="text-[#00E5FF] shrink-0 mt-0.5" />
            <p className="text-sm font-semibold group-hover:text-[#00E5FF] transition-colors">
              {office.phone}
            </p>
          </a>

          <a
            href={office.mapLink}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-auto inline-flex items-center gap-2 self-start px-4 py-2 rounded-xl bg-white/5 hover:bg-[#00E5FF] hover:text-black border border-white/10 text-sm font-semibold transition-all"
            data-testid={`office-directions-${office.key}`}
          >
            Otvoriť v Google Maps <ExternalLink size={14} />
          </a>
        </div>
      </div>
    </motion.div>
  );
}
